import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useForm, useWatch, Controller } from "react-hook-form";
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Select,
  OutlinedInput,
  InputLabel,
  FormControl,
  FormHelperText,
  Radio,
  Checkbox,
  Divider,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import LoadingScreen from "src/components/loading-screen";
import EventImageLayout from "src/layouts/event";
import { PrimaryLoadingButton, SecondaryButton } from "../listing-details/listingButtons";
import { useSnackbar } from "notistack";
import { CREATE_CART_ITEMS, DELETE_CART_ITEM } from "src/graphql/mutations";
import { CREATE_BOOKING_HOLDING_FEE_USER } from "src/graphql/mutations/bookings";
import { useMutationWithAuth, useQueryWithAuth } from "src/graphql/instances";
import { ANSWER_QUESTIONNAIRE } from "src/graphql/mutations/questionnaire";
import Iconify from "src/components/iconify";
import { GET_CART } from "src/graphql/queries";

const TEXT_MAX_LENGTH = 250;
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;

const pendingKey = (pending) => `${pending.id}__${pending.eventSessionId ?? ""}`;

const orderFieldName = (questionId) => `o_${questionId}`;
const ticketFieldName = (itemKey, localSlotIndex, questionId) =>
  `t_${itemKey}__${localSlotIndex}__${questionId}`;

const emptyValueForType = (type) => (type === "MULTIPLE_CHOICE" ? [] : type === "DATE" ? null : "");

const isValueEmpty = (value) =>
  value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

function evaluateRules(rules, answerMap, optionLabelsBySourceQuestion) {
  if (!rules?.length) return true;
  return rules.every((rule) => {
    const sourceValue = answerMap.get(rule.sourceQuestionId);
    if (sourceValue === undefined || sourceValue === null || sourceValue === "") return false;

    const labelMap = optionLabelsBySourceQuestion.get(rule.sourceQuestionId) ?? new Map();
    const allowedLabels = rule.allowedValues.map((id) => labelMap.get(id)).filter(Boolean);

    if (Array.isArray(sourceValue)) return sourceValue.some((v) => allowedLabels.includes(v));
    return allowedLabels.includes(sourceValue);
  });
}

function getOptionState(option, answerMap, optionLabelsBySourceQuestion) {
  if (option.visibilityRules?.length) {
    const ruleVisible = evaluateRules(option.visibilityRules, answerMap, optionLabelsBySourceQuestion);
    if (!ruleVisible) return "HIDDEN";
  }
  return option.visibility;
}

function Linkify({ text, sx }) {
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Box
            key={i}
            component="a"
            href={part.startsWith("www.") ? `https://${part}` : part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ color: "#007AFF", textDecoration: "underline", wordBreak: "break-word", ...sx }}
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </>
  );
}

const OptionRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "disabledOption",
})(({ theme, selected, disabledOption }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  maxWidth: "100%",
  borderRadius: 12,
  padding: theme.spacing(1.5, 1.5),
  cursor: disabledOption ? "not-allowed" : "pointer",
  userSelect: "none",
  opacity: disabledOption ? 0.45 : 1,
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(10px)",
  border: selected ? "1px solid rgba(255,255,255,0.9)" : "1px solid rgba(255,255,255,0.08)",
  transition: "background 0.15s ease, border-color 0.15s ease",
  "&:hover": disabledOption ? {} : { background: "rgba(255,255,255,0.1)" },
}));

const pillControlSx = {
  p: 0,
  pointerEvents: "none",
  color: "rgba(255,255,255,0.4)",
  "&.Mui-checked": { color: "#fff" },
};

export default function QuestionnairePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const { eventData, pendingCartItems, pendingBooking, checkoutUrl } = location.state || {};

  const { data: cartData, loading: cartLoading } = useQueryWithAuth(GET_CART, { fetchPolicy: "no-cache" });
  const [createCartItems] = useMutationWithAuth(CREATE_CART_ITEMS);
  const [deleteCartItem] = useMutationWithAuth(DELETE_CART_ITEM);
  const [createBookingHoldingFee] = useMutationWithAuth(CREATE_BOOKING_HOLDING_FEE_USER);
  const [answerQuestionnaire] = useMutationWithAuth(ANSWER_QUESTIONNAIRE);

  const [phase, setPhase] = useState("ready");

  const pages = useMemo(() => {
    const rawPages = [...(eventData?.questionnaire?.pages ?? [])].sort((a, b) => a.order - b.order);
    return rawPages.map((page) => {
      const sortedQuestions = [...(page.questions ?? [])].sort((a, b) => a.order - b.order);
      return {
        ...page,
        orderQuestions: sortedQuestions.filter((q) => q.scope === "ORDER"),
        ticketQuestions: sortedQuestions.filter((q) => q.scope === "TICKET"),
      };
    });
  }, [eventData]);

  const allQuestions = useMemo(() => pages.flatMap((p) => [...p.orderQuestions, ...p.ticketQuestions]), [pages]);
  const allOrderQuestions = useMemo(() => pages.flatMap((p) => p.orderQuestions), [pages]);
  const allTicketQuestions = useMemo(() => pages.flatMap((p) => p.ticketQuestions), [pages]);

  const optionLabelsBySourceQuestion = useMemo(() => {
    const map = new Map();
    allQuestions.forEach((q) => map.set(q.id, new Map((q.options ?? []).map((o) => [o.id, o.label]))));
    return map;
  }, [allQuestions]);

  const ticketSlots = useMemo(() => {
    const slots = [];
    let globalIndex = 0;
    (pendingCartItems ?? []).forEach((pending) => {
      const ticketType = eventData?.tickets?.ticketTypes?.find((tt) => tt.id === pending.id);
      const itemKey = pendingKey(pending);
      for (let localSlotIndex = 0; localSlotIndex < pending.quantity; localSlotIndex++) {
        globalIndex += 1;
        slots.push({ itemKey, pending, ticketTypeName: ticketType?.name, localSlotIndex, globalIndex });
      }
    });
    return slots;
  }, [pendingCartItems, eventData]);

  const existingCartItemsByKey = useMemo(() => {
    const map = new Map();
    (cartData?.cart?.items ?? []).forEach((item) => {
      if (!item.ticketType) return;
      map.set(`${item.ticketType.id}__${item.eventSession?.id ?? ""}`, item);
    });
    return map;
  }, [cartData]);

  const existingOrderAnswers = useMemo(() => {
    const map = new Map();
    (cartData?.cart?.questionnaireAnswers ?? []).forEach((a) => map.set(a.question.id, a.value));
    return map;
  }, [cartData]);

  const existingSlotAnswers = useCallback(
    (itemKey, localSlotIndex) => {
      const item = existingCartItemsByKey.get(itemKey);
      if (!item) return null;
      const map = new Map();
      (item.questionnaireAnswers ?? [])
        .filter((a) => a.localSlotIndex === localSlotIndex)
        .forEach((a) => map.set(a.question.id, a.value));
      return map;
    },
    [existingCartItemsByKey]
  );

  const defaultValues = useMemo(() => {
    const values = {};
    allOrderQuestions.forEach((q) => {
      values[orderFieldName(q.id)] = existingOrderAnswers.has(q.id)
        ? existingOrderAnswers.get(q.id)
        : emptyValueForType(q.type);
    });
    ticketSlots.forEach((slot) => {
      const slotMap = existingSlotAnswers(slot.itemKey, slot.localSlotIndex);
      allTicketQuestions.forEach((q) => {
        const fieldName = ticketFieldName(slot.itemKey, slot.localSlotIndex, q.id);
        values[fieldName] = slotMap?.has(q.id) ? slotMap.get(q.id) : emptyValueForType(q.type);
      });
    });
    return values;
  }, [allOrderQuestions, allTicketQuestions, ticketSlots, existingOrderAnswers, existingSlotAnswers]);

  const { control, getValues, setValue, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues,
  });

  const resetKeyRef = useRef(null);
  useEffect(() => {
    if (cartLoading) return;
    const key = `${allOrderQuestions.length}:${allTicketQuestions.length}:${ticketSlots.length}:loaded`;
    if (resetKeyRef.current !== key) {
      resetKeyRef.current = key;
      Object.entries(defaultValues).forEach(([name, value]) => setValue(name, value));
    }
  }, [cartLoading, defaultValues, allOrderQuestions.length, allTicketQuestions.length, ticketSlots.length, setValue]);

  const liveValues = useWatch({ control });

  const orderAnswerMap = useMemo(() => {
    const map = new Map();
    allOrderQuestions.forEach((q) => map.set(q.id, liveValues?.[orderFieldName(q.id)]));
    return map;
  }, [allOrderQuestions, liveValues]);

  const slotAnswerMap = useCallback(
    (slot) => {
      const map = new Map(orderAnswerMap);
      allTicketQuestions.forEach((q) =>
        map.set(q.id, liveValues?.[ticketFieldName(slot.itemKey, slot.localSlotIndex, q.id)])
      );
      return map;
    },
    [orderAnswerMap, allTicketQuestions, liveValues]
  );

  const isOrderQuestionVisible = useCallback(
    (question) => evaluateRules(question.visibilityRules, orderAnswerMap, optionLabelsBySourceQuestion),
    [orderAnswerMap, optionLabelsBySourceQuestion]
  );
  const isTicketQuestionVisible = useCallback(
    (question, slot) => evaluateRules(question.visibilityRules, slotAnswerMap(slot), optionLabelsBySourceQuestion),
    [slotAnswerMap, optionLabelsBySourceQuestion]
  );

  useEffect(() => {
    if (cartLoading) return;

    allOrderQuestions.forEach((q) => {
      const fieldName = orderFieldName(q.id);
      const current = liveValues?.[fieldName];
      if (!isOrderQuestionVisible(q)) {
        if (!isValueEmpty(current)) setValue(fieldName, emptyValueForType(q.type));
        return;
      }
      if (q.options?.length) {
        const cleaned = sanitizeSelection(q, current, orderAnswerMap, optionLabelsBySourceQuestion);
        if (cleaned !== current) setValue(fieldName, cleaned);
      }
    });

    ticketSlots.forEach((slot) => {
      allTicketQuestions.forEach((q) => {
        const fieldName = ticketFieldName(slot.itemKey, slot.localSlotIndex, q.id);
        const current = liveValues?.[fieldName];
        if (!isTicketQuestionVisible(q, slot)) {
          if (!isValueEmpty(current)) setValue(fieldName, emptyValueForType(q.type));
          return;
        }
        if (q.options?.length) {
          const map = slotAnswerMap(slot);
          const cleaned = sanitizeSelection(q, current, map, optionLabelsBySourceQuestion);
          if (cleaned !== current) setValue(fieldName, cleaned);
        }
      });
    });
  }, [liveValues, cartLoading]);

  function sanitizeSelection(question, value, answerMap, labelMap) {
    const selectable = (opt) => getOptionState(opt, answerMap, labelMap) === "VISIBLE";
    if (question.type === "MULTIPLE_CHOICE") {
      const arr = value ?? [];
      const filtered = arr.filter((label) => question.options.some((o) => o.label === label && selectable(o)));
      return filtered.length === arr.length ? value : filtered;
    }
    if (question.type === "SINGLE_CHOICE" || question.type === "DROPDOWN") {
      if (!value) return value;
      const stillOk = question.options.some((o) => o.label === value && selectable(o));
      return stillOk ? value : "";
    }
    return value;
  }

  const visiblePages = useMemo(() => {
    return pages.filter((page) => {
      const hasOrder = page.orderQuestions.some((q) => isOrderQuestionVisible(q));
      const hasTicket =
        page.ticketQuestions.length > 0 &&
        ticketSlots.some((slot) => page.ticketQuestions.some((q) => isTicketQuestionVisible(q, slot)));
      return hasOrder || hasTicket;
    });
  }, [pages, liveValues, ticketSlots]);

  const [currentPageId, setCurrentPageId] = useState(null);
  useEffect(() => {
    if (cartLoading || !visiblePages.length) return;
    if (!currentPageId || !visiblePages.some((p) => p.id === currentPageId)) {
      setCurrentPageId(visiblePages[0].id);
    }
  }, [cartLoading, visiblePages]);

  const currentPageIndex = Math.max(0, visiblePages.findIndex((p) => p.id === currentPageId));
  const currentPage = visiblePages[currentPageIndex];
  const isLastPage = currentPageIndex === visiblePages.length - 1;


  const validateCurrentPage = () => {
    if (!currentPage) return true;
    let firstInvalidField = null;
    clearErrors();

    currentPage.orderQuestions.forEach((q) => {
      const fieldName = orderFieldName(q.id);
      if (!isOrderQuestionVisible(q)) return;
      const value = getValues(fieldName);
      if (q.required && isValueEmpty(value)) {
        setError(fieldName, { message: t("eventManager.questionnaire.required") });
        firstInvalidField = firstInvalidField ?? fieldName;
      }
    });

    ticketSlots.forEach((slot) => {
      currentPage.ticketQuestions.forEach((q) => {
        const fieldName = ticketFieldName(slot.itemKey, slot.localSlotIndex, q.id);
        if (!isTicketQuestionVisible(q, slot)) return;
        const value = getValues(fieldName);
        if (q.required && isValueEmpty(value)) {
          setError(fieldName, { message: t("eventManager.questionnaire.required") });
          firstInvalidField = firstInvalidField ?? fieldName;
        }
      });
    });

    if (firstInvalidField) {
      const el = document.getElementById(`question-${firstInvalidField}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const goBack = () => {
    if (currentPageIndex === 0) {
      navigate(-1);
      return;
    }
    setCurrentPageId(visiblePages[currentPageIndex - 1].id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = async () => {
    if (!validateCurrentPage()) return;

    if (!isLastPage) {
      setCurrentPageId(visiblePages[currentPageIndex + 1].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    await submitQuestionnaire();
  };

  const submitQuestionnaire = async () => {
    setPhase("submitting");

    let createdCartItemIds = [];

    try {
      const existingForEvent = (cartData?.cart?.items ?? []).filter(
        (it) => it.ticketType?.eventId === eventData?.general?.id
      );
      for (const it of existingForEvent) {
        try {
          await deleteCartItem({ variables: { cartItemId: it.id } });
        } catch (e) {
          // if it's already gone (e.g. race with another tab), that's fine — proceed
          console.log("Could not delete existing cart item, proceeding anyway", e);
        }
      }

      const { data } = await createCartItems({ variables: { cartItems: pendingCartItems } });
      const createdItems = data?.createCartItems?.items ?? [];

      const createdByKey = new Map();
      pendingCartItems.forEach((pending) => {
        const created = createdItems.find(
          (it) =>
            it.ticketType?.id === pending.id &&
            (pending.eventSessionId == null || it.eventSession?.id === pending.eventSessionId)
        );
        if (created) createdByKey.set(pendingKey(pending), created.id);
      });

      if (createdByKey.size !== pendingCartItems.length) {
        throw new Error("Could not create all cart items for questionnaire");
      }
      createdCartItemIds = Array.from(createdByKey.values());

      const orderAnswers = allOrderQuestions
        .filter((q) => isOrderQuestionVisible(q))
        .map((q) => ({ questionId: q.id, value: getValues(orderFieldName(q.id)) }));

      const itemAnswers = pendingCartItems.map((pending) => {
        const itemKey = pendingKey(pending);
        return {
          cartItemId: createdByKey.get(itemKey),
          slots: Array.from({ length: pending.quantity }, (_, localSlotIndex) => {
            const slot = { itemKey, localSlotIndex };
            return {
              localSlotIndex,
              answers: allTicketQuestions
                .filter((q) => isTicketQuestionVisible(q, slot))
                .map((q) => ({
                  questionId: q.id,
                  value: getValues(ticketFieldName(itemKey, localSlotIndex, q.id)),
                })),
            };
          }),
        };
      });

      await answerQuestionnaire({ variables: { orderAnswers, itemAnswers } });

      if (pendingBooking) {
        await createBookingHoldingFee({ variables: { metadata: { booking: pendingBooking } } });
      }

      navigate(checkoutUrl);
    } catch (e) {
      console.log(e);
      setPhase("ready");

      if (createdCartItemIds.length) {
        try {
          await Promise.all(createdCartItemIds.map((id) => deleteCartItem({ variables: { cartItemId: id } })));
        } catch (cleanupError) {
          console.error("Failed to roll back cart items after questionnaire error", cleanupError);
        }
      }

      enqueueSnackbar(t("eventManager.questionnaire.submitError"), { variant: "error" });
    }
  };

  if (!eventData || cartLoading) {
    return <LoadingScreen />;
  }

  return (
    <EventImageLayout
      title={`Questionnaire ${eventData?.general?.name}`}
      image={eventData?.general?.images[0]}
      fields={eventData?.general?.fields}
      useFullHeight={true}
      stretchBackgroundToContent={true}
    >
      <Box onClick={goBack} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
        <Iconify icon="eva:chevron-left-fill" width={40} sx={{ zIndex: 2 }} />
        <Typography variant="h5" color={theme.palette.text.primary} sx={{ zIndex: 2, mx: "auto" }}>
          {t("eventManager.questionnaire.formTitle")}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 640, mx: "auto", px: 2, py: 4, zIndex: 3 }}>
        {visiblePages.length > 1 && currentPage && (
          <PageProgress currentIndex={currentPageIndex} pages={visiblePages} t={t} />
        )}

        <Stack spacing={5}>
          {currentPage?.orderQuestions.map(
            (question) =>
              isOrderQuestionVisible(question) && (
                <QuestionField
                  key={question.id}
                  fieldName={orderFieldName(question.id)}
                  question={question}
                  control={control}
                  error={errors[orderFieldName(question.id)]}
                  answerMap={orderAnswerMap}
                  optionLabelsBySourceQuestion={optionLabelsBySourceQuestion}
                  t={t}
                />
              )
          )}

          {currentPage?.ticketQuestions.length > 0 && (
            <Stack spacing={4}>
              {currentPage.orderQuestions.length > 0 && <Divider sx={{ opacity: 0.3 }} />}
              {ticketSlots.map((slot) => {
                const visibleTicketQuestions = currentPage.ticketQuestions.filter((q) =>
                  isTicketQuestionVisible(q, slot)
                );
                if (!visibleTicketQuestions.length) return null;
                const map = slotAnswerMap(slot);
                return (
                  <Box key={`${slot.itemKey}_${slot.localSlotIndex}`}>
                    <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.8, fontFamily: "Visby" }}>
                      {t("eventManager.questionnaire.ticketSlotLabel", {
                        defaultValue: "Ticket {{n}}",
                        n: slot.globalIndex,
                      })}
                      {slot.ticketTypeName ? ` · ${slot.ticketTypeName}` : ""}
                    </Typography>
                    <Stack spacing={5}>
                      {visibleTicketQuestions.map((question) => {
                        const fieldName = ticketFieldName(slot.itemKey, slot.localSlotIndex, question.id);
                        return (
                          <QuestionField
                            key={fieldName}
                            fieldName={fieldName}
                            question={question}
                            control={control}
                            error={errors[fieldName]}
                            answerMap={map}
                            optionLabelsBySourceQuestion={optionLabelsBySourceQuestion}
                            t={t}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}

          <Box sx={{ display: "flex", justifyContent: "right", gap: 2 }}>
            <SecondaryButton
              variant="outlined"
              color="inherit"
              size="large"
              onClick={goBack}
              sx={{ backgroundColor: "transparent" }}
              disabled={phase === "submitting"}
            >
              {t("bookings.back")}
            </SecondaryButton>

            <PrimaryLoadingButton size="large" loading={phase === "submitting"} onClick={goNext}>
              {isLastPage ? t("productPage.continue") : t("bookings.continue")}
            </PrimaryLoadingButton>
          </Box>
        </Stack>
      </Box>
    </EventImageLayout>
  );
}

function PageProgress({ currentIndex, pages, t }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {t("eventManager.questionnaire.pageProgress", {
          current: currentIndex + 1,
          total: pages.length,
        })}
        {pages[currentIndex]?.title ? ` · ${pages[currentIndex].title}` : ""}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, mt: 1 }}>
        {pages.map((page, i) => (
          <Box
            key={page.id}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: i <= currentIndex ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.15)",
              transition: "background 0.2s ease",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function QuestionField({ fieldName, question, control, error, answerMap, optionLabelsBySourceQuestion, t }) {
  const optionState = (opt) => getOptionState(opt, answerMap, optionLabelsBySourceQuestion);
  const visibleOptions = (question.options ?? []).filter((opt) => optionState(opt) !== "HIDDEN");

  return (
    <Box id={`question-${fieldName}`}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontFamily: "Visby" }}>
        {question.title}
        {question.required && (
          <Typography component="span" color="error">
            {" *"}
          </Typography>
        )}
      </Typography>
      {question.description && (
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, fontFamily: "Visby" }}>
          <Linkify text={question.description} />
        </Typography>
      )}

      {question.type === "TEXT" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => {
            const value = field.value ?? "";
            return (
              <Box sx={{ mt: 1 }}>
                <TextField
                  {...field}
                  value={value}
                  fullWidth
                  multiline
                  minRows={1}
                  inputProps={{ maxLength: TEXT_MAX_LENGTH }}
                  error={!!error}
                  helperText={error?.message}
                />
                <Typography variant="caption" color="grey" sx={{ ml: 0.5 }}>
                  {`${value.length}/${TEXT_MAX_LENGTH} ${t("bookings.characters")}`}
                </Typography>
              </Box>
            );
          }}
        />
      )}

      {question.type === "DATE" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => field.onChange(date && date.isValid() ? date.format("YYYY-MM-DD") : null)}
                format="D/M/YYYY"
                sx={{ mt: 1, width: "100%" }}
                slotProps={{ textField: { fullWidth: true, error: !!error, helperText: error?.message } }}
              />
            </LocalizationProvider>
          )}
        />
      )}

      {question.type === "DROPDOWN" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!error} sx={{ mt: 1 }}>
              <InputLabel sx={{ "&.Mui-focused": { color: "text.primary" } }}>
                {t("eventManager.questionnaire.selectOption")}
              </InputLabel>
              <Select
                {...field}
                input={<OutlinedInput label={t("eventManager.questionnaire.selectOption")} />}
                renderValue={(val) => val}
              >
                {visibleOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.label} disabled={optionState(opt) === "DISABLED"}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      )}

      {question.type === "SINGLE_CHOICE" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <Stack spacing={1.25} sx={{ mt: 1.5, alignItems: "flex-start" }}>
              {visibleOptions.map((opt) => {
                const isSelected = field.value === opt.label;
                const disabledOption = optionState(opt) === "DISABLED";
                return (
                  <OptionRow
                    key={opt.id}
                    selected={isSelected}
                    disabledOption={disabledOption}
                    onClick={() => !disabledOption && field.onChange(isSelected ? "" : opt.label)}
                  >
                    <Radio sx={pillControlSx} checked={isSelected} disabled={disabledOption} />
                    <Typography sx={{ fontSize: "0.95rem", mr: 1 }}>{opt.label}</Typography>
                  </OptionRow>
                );
              })}
              {error && (
                <FormHelperText error sx={{ ml: 1 }}>
                  {error.message}
                </FormHelperText>
              )}
            </Stack>
          )}
        />
      )}

      {question.type === "MULTIPLE_CHOICE" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => {
            const selected = field.value ?? [];
            const toggle = (label) => {
              field.onChange(selected.includes(label) ? selected.filter((v) => v !== label) : [...selected, label]);
            };
            return (
              <Stack spacing={1.25} sx={{ mt: 1.5, alignItems: "flex-start" }}>
                {visibleOptions.map((opt) => {
                  const isSelected = selected.includes(opt.label);
                  const disabledOption = optionState(opt) === "DISABLED";
                  return (
                    <OptionRow
                      key={opt.id}
                      selected={isSelected}
                      disabledOption={disabledOption}
                      onClick={() => !disabledOption && toggle(opt.label)}
                    >
                      <Checkbox sx={pillControlSx} checked={isSelected} disabled={disabledOption} />
                      <Typography sx={{ fontSize: "0.95rem", mr: 1 }}>{opt.label}</Typography>
                    </OptionRow>
                  );
                })}
                {error && (
                  <FormHelperText error sx={{ ml: 1 }}>
                    {error.message}
                  </FormHelperText>
                )}
              </Stack>
            );
          }}
        />
      )}
    </Box>
  );
}